import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "src/trackEvent";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/common/state/hooks";
import ModalStandard from "src/common/components/Modal/ModalStandard";
import { openStandardModal } from "src/common/state/slice/modal/modalSlice";
import { ScenariaPlan, ScenariaSkyboxPromptPreviewScene } from "src/model/model";
import { IMedia } from "src/model/unityObject";
import { getScenarioHelperContextImages } from "../../state/chatbotSlice";
import DecorStatusRow from "../referenceImage/DecorStatusRow";
import StagedReferenceImageCard from "../referenceImage/StagedReferenceImageCard";
import useRetryImageAnalysis from "../referenceImage/useRetryImageAnalysis";
import OptionSection from "./OptionSection";
import useSkyboxPromptPreview from "./useSkyboxPromptPreview";

// Shows the attached reference picture whose vision description seeds the
// shared trunk (provenance viewer, opened from the legend link).
const trunkImageModalId = "blockadeTrunkImageModal";

interface BlockadeImagesSectionProps {
  plan?: ScenariaPlan;
  conversationId: number;
  enabled: boolean;
  onToggleEnabled: () => void;
  // sceneId -> full replacement prompt. An entry exists ONLY when the user's
  // text differs from the server-composed prompt (D6, ephemeral by design).
  overrides: Record<string, string>;
  onSetOverride: (sceneId: string, text: string) => void;
  onClearOverride: (sceneId: string) => void;
  // Reference-image state + actions (owned by Chatbot). The provenance row /
  // staged card render here so the Options stage both SHOWS the decor's
  // provenance and can change it in place — no round trip to the plan.
  stagedReferenceImage: IMedia | null;
  isReferenceImageBusy: boolean;
  onIntegrateStagedImage: () => void;
  onCancelStagedImage: () => void;
  onPickReferenceImage: () => void;
  onRemoveReferenceImage: (media: IMedia) => void;
}

const CPFAG = "pages.chat.createProjectFromAiGraph";
const CG = "pages.chat.configureGeneration";

// Trunk = "the 360 image world" (image360 badge family, purple); variation =
// "per-item annotation" (interaction-chip family, amber). Appendix C — reuse
// existing tint families ONLY, no new colors.
const TRUNK_TINT = "bg-purple-50 text-purple-700 border-purple-200";
const VARIATION_TINT = "bg-amber-50 text-amber-700 border-amber-200";

// The Blockade "generate 360° images" feature as a collapsible OptionSection
// (Phase 3, D6): per-scene composed-prompt previews with a trunk/variation
// color breakdown and editable per-scene overrides. Preview is informative,
// never blocking — a fetch failure still lets generation run.
const BlockadeImagesSection = ({
  plan,
  conversationId,
  enabled,
  onToggleEnabled,
  overrides,
  onSetOverride,
  onClearOverride,
  stagedReferenceImage,
  isReferenceImageBusy,
  onIntegrateStagedImage,
  onCancelStagedImage,
  onPickReferenceImage,
  onRemoveReferenceImage,
}: BlockadeImagesSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // Attached-image media rows, hydrated on conversation load — carries the
  // s3_url the provenance modal displays (no extra fetch/signing needed).
  const contextImages = useSelector(getScenarioHelperContextImages);

  // Controlled open state lifted here so enabled || open is evaluated in the
  // same render as open — no two-render lag from a mirrored bodyOpen state.
  // Auto-syncs with enabled using the getDerivedStateFromProps pattern: updating
  // open synchronously during render (not in a useEffect) avoids the one-render
  // lag where enabled=false but open is still true, keeping useSkyboxPromptPreview
  // active one extra cycle after the checkbox is unchecked.
  const [open, setOpen] = useState(enabled);
  const [prevEnabled, setPrevEnabled] = useState(enabled);
  if (prevEnabled !== enabled) {
    setPrevEnabled(enabled);
    setOpen(enabled);
  }

  // Which rows have their edit textarea open. Purely local UI — the committed
  // override lives in the parent's `overrides` record.
  const [editing, setEditing] = useState<Set<string>>(new Set());
  // Per-scene in-progress text while the textarea is open. Cleared when editing
  // is toggled or reset so reopening always starts from the current committed value.
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});

  const toggleEditing = (sceneId: string) => {
    setEditing((prev) => {
      const next = new Set(prev);
      if (next.has(sceneId)) next.delete(sceneId);
      else next.add(sceneId);
      return next;
    });
    setDraftValues((prev) => {
      const next = { ...prev };
      delete next[sceneId];
      return next;
    });
  };

  const scenes = plan?.sceneOutline ?? [];
  const imageSceneCount = scenes.filter((s) => s.mediaType === "image360").length;

  // Re-fetch only when the plan actually changes; the fetch fires while the
  // section is enabled OR merely peeked open while off (D4). Indifferent users
  // who never expand still trigger no request.
  const planStamp = plan?.lastRefinedAt ?? plan?.generatedAt;
  const {
    status,
    scenes: previewScenes,
    trunk,
    retry,
    refresh,
  } = useSkyboxPromptPreview(conversationId, planStamp, enabled || open);

  // Provenance: the attached picture whose vision description IS the shared
  // trunk. Undefined when the trunk is LLM-synthesized (visualEnvironment /
  // last resort) or the media is no longer in the hydrated conversation.
  const trunkMedia =
    trunk?.source === "attachedImages"
      ? contextImages.find((m) => trunk.mediaIds?.includes(m.id))
      : undefined;

  // Own hook instance (vs the plan-card's): a successful retry here must also
  // bust the skybox preview cache so the upgraded trunk shows up in the
  // per-scene prompts below.
  const { isRetrying: isRetryingVision, retry: retryImageAnalysis } = useRetryImageAnalysis(
    conversationId,
    refresh,
  );

  const previewById = useMemo(() => {
    const map: Record<string, ScenariaSkyboxPromptPreviewScene> = {};
    previewScenes.forEach((s) => {
      map[s.sceneId] = s;
    });
    return map;
  }, [previewScenes]);

  // Usage analytics (Phase 4.5): record when the Blockade 360°-image feature is
  // toggled, and to which state. Wrapped here rather than in the reusable
  // OptionSection so only this feature's toggle is tracked. `!enabled` = the
  // state after the flip. No identifiers — aggregate boolean only.
  const handleToggleEnabled = () => {
    trackEvent("blockade-toggle", { enabled: !enabled });
    onToggleEnabled();
  };

  const summary = enabled
    ? t(`${CG}.blockadeSummaryOn`, { count: imageSceneCount })
    : t(`${CG}.blockadeSummaryOff`);

  // Commit-on-change: an override entry exists only while the text differs from
  // the server prompt; typing back to the server text drops the entry (D6).
  const commit = (sceneId: string, text: string, serverPrompt: string) => {
    if (text.trim() && text !== serverPrompt) onSetOverride(sceneId, text);
    else onClearOverride(sceneId);
  };

  const reset = (sceneId: string) => {
    onClearOverride(sceneId);
    setDraftValues((prev) => {
      const next = { ...prev };
      delete next[sceneId];
      return next;
    });
    setEditing((prev) => {
      const next = new Set(prev);
      next.delete(sceneId);
      return next;
    });
  };

  const inputClasses =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500";

  const renderPromptBody = (
    scene: ScenariaSkyboxPromptPreviewScene | undefined,
    overrideText: string | undefined,
  ) => {
    // A user-authored override has no trunk/variation decomposition — the
    // "modifié" badge explains why it renders untinted.
    if (overrideText !== undefined) {
      return <p className="text-sm leading-snug text-gray-700">{overrideText}</p>;
    }
    if (!scene || !scene.prompt) return null;
    if (scene.source === "b2" && scene.trunk && scene.variation) {
      return (
        <p className="text-sm leading-snug">
          <span className={`rounded border px-1 ${TRUNK_TINT}`}>{scene.trunk}</span>
          <span className="text-gray-500">{", "}</span>
          <span className={`rounded border px-1 ${VARIATION_TINT}`}>{scene.variation}</span>
        </p>
      );
    }
    // "seed" / "fallback" — a single prompt with no shared/variation split.
    return (
      <>
        <p className="text-sm leading-snug text-gray-700">{scene.prompt}</p>
        <p className="text-[10px] text-gray-400 mt-0.5">{t(`${CG}.promptNotDecomposable`)}</p>
      </>
    );
  };

  return (
    <OptionSection
      id="blockade-images"
      title={t(`${CPFAG}.generateBlockadeImages`)}
      enabled={enabled}
      onToggleEnabled={handleToggleEnabled}
      summary={summary}
      open={open}
      onOpenChange={setOpen}
    >
      {/* Intro — states what the feature produces (information scent). Reads as
          a preview-with-call-to-enable while off, as an editing hint while on. */}
      <p className="text-xs text-gray-600 mb-2">
        {enabled ? t(`${CG}.blockadeIntro`) : t(`${CG}.blockadeIntroDisabled`)}
      </p>

      {/* Decor provenance — the same first-class reference-image object as the
          plan card. A picked-but-not-integrated image renders the loud staged
          card (its CTA fires the realign right from here); otherwise the
          server-truth row, with in-place pick/change/remove: attaching an
          image is no longer a round trip through the plan. Peek-while-off
          stays read-only for per-scene edits (D4), but the reference image is
          plan-level state, so its controls stay live either way. */}
      {stagedReferenceImage ? (
        <StagedReferenceImageCard
          media={stagedReferenceImage}
          busy={isReferenceImageBusy}
          onIntegrate={onIntegrateStagedImage}
          onCancel={onCancelStagedImage}
        />
      ) : (
        <DecorStatusRow
          plan={plan}
          contextImages={contextImages}
          busy={isReferenceImageBusy}
          onPick={onPickReferenceImage}
          onRemove={onRemoveReferenceImage}
          onView={trunkMedia ? () => dispatch(openStandardModal(trunkImageModalId)) : undefined}
          onRetryAnalysis={retryImageAnalysis}
          isRetryingAnalysis={isRetryingVision}
          className="mb-2 rounded-md bg-purple-50/40 px-2 py-1.5"
        />
      )}

      {/* Legend — decodes the color breakdown (Appendix C). Only meaningful once
          the previews are ready; noise during loading/error. Provenance (whose
          image feeds the trunk) now lives in the row above, so the chips stay
          purely lexical. */}
      {status === "ready" && (
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${TRUNK_TINT}`}>
            {t(`${CG}.legendTrunk`)}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${VARIATION_TINT}`}>
            {t(`${CG}.legendVariation`)}
          </span>
        </div>
      )}

      {/* RESERVED SLOT (D9 / docs/proposals/scenaria-image-coherency-seed-lock-
          and-hub-remix.md): the future "stronger visual consistency" (hub-remix)
          checkbox — wired to generationOptions.blockade.strongConsistency — goes
          here, above the scene list. Comment only; no dead UI yet. */}

      {status === "loading" && (
        <div className="space-y-2" aria-hidden>
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-gray-50 rounded-md p-2.5">
              <div className="animate-pulse bg-gray-100 rounded h-4 w-1/3 mb-2" />
              <div className="animate-pulse bg-gray-100 rounded h-4 w-full" />
            </div>
          ))}
        </div>
      )}

      {status === "error" && (
        <div className="text-sm text-gray-600">
          <p>{t(`${CG}.previewError`)}</p>
          <button
            type="button"
            onClick={retry}
            className="text-purple-500 hover:text-purple-600 font-medium text-xs mt-1"
          >
            {t(`${CG}.previewRetry`)}
          </button>
        </div>
      )}

      {status === "ready" && (
        <div className="space-y-1.5">
          {scenes.map((scene, i) => {
            if (scene.mediaType === "video360") {
              return (
                <div key={scene.sceneId || i} className="bg-gray-50 rounded-md p-2.5 opacity-60">
                  <p className="text-sm font-semibold text-gray-800">{scene.title}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    {t(`${CG}.noImageForVideo360`)}
                  </p>
                </div>
              );
            }

            const preview = previewById[scene.sceneId];
            const serverPrompt = preview?.prompt ?? "";
            const hasOverride = Object.prototype.hasOwnProperty.call(overrides, scene.sceneId);
            const isEditing = editing.has(scene.sceneId);
            const value = hasOverride ? overrides[scene.sceneId] : serverPrompt;
            const draft = draftValues[scene.sceneId];
            const textareaValue = draft !== undefined ? draft : value;

            return (
              <div key={scene.sceneId || i} className="bg-gray-50 rounded-md p-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-800">{scene.title}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    {hasOverride && (
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded border ${VARIATION_TINT}`}
                      >
                        {t(`${CG}.overriddenBadge`)}
                      </span>
                    )}
                    {/* Editing is a committed-user activity; a peek while off is
                        read-only (D4). Existing overrides still render below. */}
                    {enabled && (
                      <button
                        type="button"
                        onClick={() => toggleEditing(scene.sceneId)}
                        className="text-purple-500 hover:text-purple-600 font-medium text-xs"
                      >
                        {t("general.edit")}
                      </button>
                    )}
                    {enabled && hasOverride && (
                      <button
                        type="button"
                        onClick={() => reset(scene.sceneId)}
                        className="text-purple-500 hover:text-purple-600 font-medium text-xs"
                      >
                        {t(`${CG}.resetOverride`)}
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-1.5">
                  {enabled && isEditing ? (
                    <textarea
                      rows={2}
                      maxLength={400}
                      value={textareaValue}
                      onChange={(e) =>
                        setDraftValues((prev) => ({
                          ...prev,
                          [scene.sceneId]: e.target.value,
                        }))
                      }
                      onBlur={() => commit(scene.sceneId, textareaValue, serverPrompt)}
                      className={inputClasses}
                    />
                  ) : (
                    renderPromptBody(preview, hasOverride ? value : undefined)
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Provenance viewer — the attached picture whose vision description
          seeds the shared trunk. Opened from the legend's "view image" link. */}
      {trunkMedia && (
        <ModalStandard id={trunkImageModalId} modalType="standard" className="w-[36rem]">
          <div className="p-2 space-y-2">
            <p className="text-sm font-semibold text-gray-800">
              {trunkMedia.filename || trunkMedia.name}
            </p>
            <img
              className="object-contain w-full max-h-[60vh] rounded-md"
              src={trunkMedia.s3_url}
              alt={trunkMedia.filename || trunkMedia.name}
            />
            <p className="text-xs text-gray-500">{t(`${CG}.trunkImageModalHint`)}</p>
          </div>
        </ModalStandard>
      )}
    </OptionSection>
  );
};

export default BlockadeImagesSection;
